import { useState } from "react";
import { fieldLabel, partyColor } from "../lib/palette";
import type { SignField, SignParty } from "../types";
import { FieldGlyph } from "../ui/icons";

function rankLabel(i: number): string {
  return i === 0 ? "1re partie" : `${i + 1}e partie`;
}

function FieldRow({
  f,
  i,
  n,
  parties,
  sel,
  frozen,
  canUp,
  canDown,
  dragging,
  over,
  onPick,
  onRename,
  onRequired,
  onDelete,
  onMove,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
}: {
  f: SignField;
  i: number;
  n: number;
  parties: SignParty[];
  sel: number;
  frozen: boolean;
  canUp: boolean;
  canDown: boolean;
  dragging: boolean;
  over: boolean;
  onPick: (index: number) => void;
  onRename: (index: number, name: string) => void;
  onRequired: (index: number) => void;
  onDelete: (index: number) => void;
  onMove: (from: number, dir: "up" | "down") => void;
  onDragStart: (index: number) => void;
  onDragOver: (index: number) => void;
  onDrop: (index: number) => void;
  onDragEnd: () => void;
}) {
  return (
    <li
      className={`${sel === i ? "is-on" : ""}${dragging ? " is-drag" : ""}${over ? " is-over" : ""}`}
      draggable={!frozen}
      onDragStart={(e) => {
        e.dataTransfer.setData("text/plain", String(i));
        e.dataTransfer.effectAllowed = "move";
        onDragStart(i);
      }}
      onDragOver={(e) => {
        if (frozen) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        onDragOver(i);
      }}
      onDrop={(e) => {
        e.preventDefault();
        onDrop(i);
      }}
      onDragEnd={onDragEnd}
    >
      {!frozen ? (
        <span className="sg-placed__num" aria-hidden="true">
          {n}
        </span>
      ) : null}
      <button type="button" className="sg-placed__pick" onClick={() => onPick(i)} aria-label={`Sélectionner ${f.label || fieldLabel(f.kind)}`}>
        <em className="sg-placed__glyph" style={{ color: partyColor(parties, f.party_id) }}>
          <FieldGlyph kind={f.kind} />
        </em>
      </button>
      <input
        value={f.label ?? fieldLabel(f.kind)}
        disabled={frozen}
        aria-label="Nom de l’élément"
        onFocus={() => onPick(i)}
        onChange={(e) => onRename(i, e.target.value)}
        onBlur={(e) => {
          if (!e.target.value.trim()) onRename(i, fieldLabel(f.kind));
        }}
      />
      {!frozen ? (
        <span className="sg-placed__ord">
          <button type="button" disabled={!canUp} aria-label="Remonter" onClick={() => onMove(i, "up")}>
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="m7 14 5-6 5 6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <button type="button" disabled={!canDown} aria-label="Descendre" onClick={() => onMove(i, "down")}>
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="m7 10 5 6 5-6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </span>
      ) : null}
      {!frozen ? (
        <button
          type="button"
          role="switch"
          className={`sg-reqsw${f.required ? " is-on" : ""}`}
          aria-checked={Boolean(f.required)}
          aria-label="Obligatoire"
          title={f.required ? "Obligatoire" : "Facultatif"}
          onClick={() => {
            onPick(i);
            onRequired(i);
          }}
        >
          <i className="sg-reqsw__knob" aria-hidden="true" />
        </button>
      ) : null}
      {!frozen ? (
        <button type="button" className="sg-iconbtn is-del" aria-label="Supprimer l’élément" onClick={() => onDelete(i)}>
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M6 6l12 12M18 6 6 18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </button>
      ) : null}
    </li>
  );
}

export function FieldList({
  fields,
  parties,
  sel,
  frozen,
  onPick,
  onRename,
  onRequired,
  onDelete,
  onMove,
}: {
  fields: SignField[];
  parties: SignParty[];
  sel: number;
  frozen: boolean;
  onPick: (index: number) => void;
  onRename: (index: number, name: string) => void;
  onRequired: (index: number) => void;
  onDelete: (index: number) => void;
  onMove: (from: number, to: number) => void;
}) {
  const [drag, setDrag] = useState<number | null>(null);
  const [over, setOver] = useState<number | null>(null);
  const ordered = parties.slice().sort((a, b) => a.sort_order - b.sort_order);
  const group = ordered.length > 1;
  const blocks = group
    ? ordered.map((party, i) => {
        const rank = rankLabel(i);
        return {
          key: party.id,
          label: party.name && party.name !== rank ? `${rank} · ${party.name}` : rank,
          items: fields.map((f, index) => ({ f, index })).filter(({ f }) => f.party_id === party.id),
        };
      })
    : [{ key: "all", label: "", items: fields.map((f, index) => ({ f, index })) }];
  const orphan = group ? fields.map((f, index) => ({ f, index })).filter(({ f }) => !ordered.some((p) => p.id === f.party_id)) : [];
  if (orphan.length) blocks.push({ key: "other", label: "Autres", items: orphan });

  function step(index: number, dir: "up" | "down") {
    const partyId = fields[index]?.party_id;
    const siblings = fields.map((_, i) => i).filter((i) => (group ? fields[i].party_id === partyId : true));
    const pos = siblings.indexOf(index);
    const dest = siblings[pos + (dir === "up" ? -1 : 1)];
    if (dest === undefined) return;
    onMove(index, dest);
  }

  function dropOn(target: number) {
    if (drag === null || drag === target) {
      setDrag(null);
      setOver(null);
      return;
    }
    if (group && fields[drag]?.party_id !== fields[target]?.party_id) {
      setDrag(null);
      setOver(null);
      return;
    }
    onMove(drag, target);
    setDrag(null);
    setOver(null);
  }

  return (
    <div className="sg-placed">
      <strong>Éléments posés</strong>
      {fields.length === 0 ? (
        <p>Posez une case sur le PDF — elle apparaît ici.</p>
      ) : (
        blocks.map((b) =>
          b.items.length === 0 && b.key !== "all" ? null : (
            <div key={b.key} className="sg-placed__block">
              {b.label ? <h3>{b.label}</h3> : null}
              <ol>
                {b.items.map(({ f, index }, n) => (
                  <FieldRow
                    key={`${f.kind}-${f.id ?? index}`}
                    f={f}
                    i={index}
                    n={n + 1}
                    parties={parties}
                    sel={sel}
                    frozen={frozen}
                    canUp={n > 0}
                    canDown={n < b.items.length - 1}
                    dragging={drag === index}
                    over={over === index && drag !== index}
                    onPick={onPick}
                    onRename={onRename}
                    onRequired={onRequired}
                    onDelete={onDelete}
                    onMove={step}
                    onDragStart={setDrag}
                    onDragOver={setOver}
                    onDrop={dropOn}
                    onDragEnd={() => {
                      setDrag(null);
                      setOver(null);
                    }}
                  />
                ))}
              </ol>
            </div>
          ),
        )
      )}
    </div>
  );
}
